/* eslint-disable react-hooks/exhaustive-deps */
import { faArrowRight, faInfoCircle, faRefresh, faXmark } from "@fortawesome/free-solid-svg-icons";
import { Formik, useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useEffect, useState } from "react";
import { Alert, Button, Offcanvas } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import * as Yup from "yup";

import {
  SalaryDeductionsAndReliefForm,
  SalaryEmployerDetailsForm,
  SalaryExemptSection10Form,
  SalarySection17OneForm,
  SalarySection17ThreeForm,
  SalarySection17TwoForm,
  SalarySection89ANonNotifiedForm,
  SalarySection89ANotifiedForm,
  SalarySection89APreYearForm
} from "..";
import DocFilingApi from "../../../../api/DocFilingApi";
import {
  EActionItem,
  EToastvariant,
  IAddIncomeForm,
  IForm16Doc,
  ISalaryFormTypes
} from "../../../../config/interfaces";
import {
  IncomeDetailsSalaryFormInitValues,
  IncomeDetailsSalaryFormSchema
} from "../../../../pages/itr-filing/efiling.config";
import { useAppDispatch, useAppSelector } from "../../../../redux/Store";
import { showHintModal } from "../../../../redux/slice/HintModalSlice";
import { ITRFilingSelector } from "../../../../redux/slice/ITRFilingSlice";
import { showToastMsg } from "../../../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../../../services";
import FASvgIcon from "../../../FASvgIcon";
import FileUploadDropzone from "../../../FileUploadDropzone";
import PriceDisplay from "../../../PriceDisplay";
import ScrollToFirstError from "../../../ScrollToFirstError";
import { ConfirmationModal } from "../../../modals";
import { OverlaySpinner } from "../../../placeholders";
import { UploadedDocument } from "../../assisted";

type Props = {
	showCanvas: boolean;
	formValues: IAddIncomeForm | null;
	formType: ISalaryFormTypes;
	handleCloseBtnPress: (salaryData: IAddIncomeForm | null) => void;
};

const SalaryIncomeDetailsForm: React.FC<Props> = (props: Props) => {
	const { showCanvas, handleCloseBtnPress, formValues, formType } = props;

	const dispatch = useAppDispatch();
	const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);

	const [initFormType, setInitFormType] = useState<ISalaryFormTypes>(null);
	const [initFormValues, setInitFormValues] = useState<IAddIncomeForm>(
		IncomeDetailsSalaryFormInitValues
	);
	const [selectedDocuments, setSelectedDocuments] = useState<IForm16Doc[]>([]);
	const [deleteDocModal, setDeleteDocModal] = useState<IForm16Doc | null>(null);
	const [isAction, setIsAction] = useState<EActionItem>(EActionItem.NULL);
	const [showForm, setShowForm] = useState<boolean>(false);
	const [showUploadError, setShowUploadError] = useState<boolean>(false);

	useEffect(() => {
		if (showCanvas && formValues) {
			setInitFormValues(() => cloneDeep(formValues));
		} else {
			setInitFormValues(() => cloneDeep(IncomeDetailsSalaryFormInitValues));
		}
		setInitFormType(() => formType);
	}, [showCanvas, formValues]);

	useEffect(() => {
		if (initFormType === "MANUAL") {
			setShowForm(() => true);
		} else {
			setShowForm(() => false);
		}
	}, [initFormType]);

	const createSelectedFilesData = async (files: File[]) => {
		try {
			for (let index = 0; index < files.length; index++) {
				const file = files[index];

				const uuid = uuidv4().replace(/[^a-zA-Z0-9 ]/g, "");
				const fileNameHash = `${uuid}.pdf`;
				const fileData: IForm16Doc = {
					id: `${index}`,
					uuid: uuid,
					fileDispalyName: file.name,
					fileNameHash: fileNameHash,
					file: file
				};

				setSelectedDocuments((prev) => [...prev, fileData]);
			}
		} catch (error) {
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Error",
					message: UtilService.getErrorMessage(error),
					variant: EToastvariant.Danger
				})
			);
		}
	};

	const handleDeleteDocument = async () => {
		try {
			if (!deleteDocModal) {
				return;
			}

			setIsAction(EActionItem.DELETE);
			const _selectedDocuments = cloneDeep(selectedDocuments);
			_selectedDocuments.splice(Number(deleteDocModal.id), 1);
			setSelectedDocuments(() => _selectedDocuments);
			setShowUploadError(() => false);

			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Deleted",
					message: `${deleteDocModal?.fileDispalyName} deleted successfully`,
					variant: EToastvariant.Success
				})
			);
		} catch (error) {
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Error",
					message: UtilService.getErrorMessage(error),
					variant: EToastvariant.Danger
				})
			);
		} finally {
			setIsAction(EActionItem.NULL);
			setDeleteDocModal(null);
		}
	};

	const handleExtractForm16Data = async () => {
		try {
			if (!selectedDocuments?.length) {
				return;
			}

			setIsAction(EActionItem.CREATE);
			setShowForm(() => false);

			const docFilingApi = DocFilingApi.getInstance();
			const formData = new FormData();

			for (let index = 0; index < selectedDocuments.length; index++) {
				const file = selectedDocuments[index].file as File;
				const fileNameHash = selectedDocuments[index].fileNameHash;
				formData.append("form_16", file, fileNameHash);
				const respData = await docFilingApi.extractForm16Data(formData);

				if (respData.data.status === 200) {
					const newFormValues = {
						...cloneDeep(IncomeDetailsSalaryFormInitValues),
						...cloneDeep(respData.data.data)
					};
					setInitFormValues(() => cloneDeep(newFormValues));
					formData.delete("form_16");
					setInitFormType(() => "MANUAL");
					setShowForm(() => true);
				} else {
					formData.delete("form_16");
					setShowUploadError(() => true);
				}
			}
		} catch (error) {
			dispatch(
				showToastMsg({
					isVisible: true,
					title: "Error",
					message: UtilService.getErrorMessage(error),
					variant: EToastvariant.Danger
				})
			);
		} finally {
			setIsAction(EActionItem.NULL);
			setDeleteDocModal(null);
			setShowForm(() => true);
		}
	};

	return (
		<>
			<Offcanvas show={showCanvas} placement="end" backdrop={true}>
				<Offcanvas.Header>
					<Offcanvas.Title>
						{initFormType === "MANUAL"
							? "Salary Income details"
							: "Upload Form-16 to Auto-Fill Your Data"}
					</Offcanvas.Title>
					<Button
						variant="primary"
						className="icon-only hide-offcanvas"
						onClick={() => {
							handleCloseBtnPress(null);
							setTimeout(() => {
								setSelectedDocuments(() => []);
								setInitFormType(() => null);
							}, 200);
						}}
					>
						<FASvgIcon icon={faXmark} />
					</Button>
				</Offcanvas.Header>

				{initFormType === "MANUAL" && showForm ? (
					<Formik
						enableReinitialize={true}
						initialValues={initFormValues}
						validationSchema={Yup.object().shape({ ...IncomeDetailsSalaryFormSchema })}
						onSubmit={(values) => {
							handleCloseBtnPress(values);
							setTimeout(() => {
								setSelectedDocuments(() => []);
								setInitFormType(() => null);
							}, 200);
						}}
					>
						{({ handleSubmit, validateForm, submitCount, isValid, errors }) => (
							<>
								<ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />

								<Offcanvas.Body>
									<SalaryEmployerDetailsForm />
									<SalaryBreakupForm />
								</Offcanvas.Body>

								<div className="offcanvas-btns">
									{!isFilingReadOnly ? (
										<Button
											variant="outline-white"
											size="sm"
											onClick={async (event) => {
												event.preventDefault();
												await validateForm();
												handleSubmit();
											}}
										>
											Save and Close
										</Button>
									) : (
										<Button
											variant="outline-white"
											size="sm"
											onClick={() => handleCloseBtnPress(null)}
										>
											Close
										</Button>
									)}
								</div>
							</>
						)}
					</Formik>
				) : (
					<>
						<Offcanvas.Body>
							<section className="mb-5">
								{selectedDocuments?.map((doc, index) => (
									<UploadedDocument
										key={`form16-${index}`}
										uuid={doc.uuid}
										fileName={doc.fileDispalyName}
										showDeleteBtn={true}
										handleOnPressDelete={() => setDeleteDocModal(doc)}
										showViewBtn={true}
										handleOnPressView={() => {
											const file = doc.file as Blob;
											const fileUrl = URL.createObjectURL(file);
											const newWindow = window.open(fileUrl, "_blank", "noopener, noreferrer");
											if (newWindow) {
												newWindow.opener = null;
												URL.revokeObjectURL(fileUrl);
											}
										}}
									/>
								))}

								{!selectedDocuments?.length ? (
									<FileUploadDropzone
										multiple={false}
										allowedFileTypes={[".pdf"]}
										handleOnFileSelect={(files) => createSelectedFilesData(files)}
									/>
								) : null}

								{showUploadError ? (
									<>
										<Alert variant="danger" className="mb-4 size-sm">
											<h5 className="alert-title">
												There was an error processing your document. Please try again or add it
												manually.
											</h5>
										</Alert>

										<div className="btns-wrap justify-content-between">
											<Button
												variant="link"
												onClick={() => {
													setShowUploadError(() => false);
													setInitFormType(() => "MANUAL");
													setShowForm(() => true);
												}}
												size="sm"
												className="text-primary text-decoration-underline"
											>
												Add Income Manually
											</Button>
											<Button
												variant="outline-primaryDark"
												onClick={() => {
													setShowUploadError(() => false);
													handleExtractForm16Data();
												}}
												size="sm"
											>
												Try Again
												<FASvgIcon icon={faRefresh} />
											</Button>
										</div>
									</>
								) : (
									<div className="btns-wrap mt-5">
										<Button
											variant="primary"
											onClick={() => handleExtractForm16Data()}
											disabled={!selectedDocuments?.length || isAction === EActionItem.CREATE}
										>
											Continue
											<FASvgIcon icon={faArrowRight} />
										</Button>
									</div>
								)}
							</section>
						</Offcanvas.Body>
					</>
				)}
			</Offcanvas>

			{deleteDocModal && (
				<ConfirmationModal
					showModal={!!deleteDocModal}
					onYesBtnClick={() => handleDeleteDocument()}
					onNoBtnClick={() => setDeleteDocModal(null)}
					title="Please confirm"
					message={`Are you sure you want to delete ${deleteDocModal?.fileDispalyName} file?`}
					onYesBtnText="Delete"
					onNoBtnText="Cancel"
					isProcessing={isAction === EActionItem.DELETE}
				/>
			)}

			{isAction === EActionItem.CREATE ? <OverlaySpinner /> : null}
		</>
	);
};

export default SalaryIncomeDetailsForm;

const SalaryBreakupForm: React.FC = () => {
	return (
		<section className="mb-3">
			<p className="leading-para fw-bold text-primary">Salary Breakup</p>
			<GrossSalary />
			<ExemptAllowances />
			<Balance1Minus2 />
			<DeductionsAndRelief />
			<IncomeChargeableUnderHeadSalaries />
		</section>
	);
};

const GrossSalary = () => {
	const { values, setFieldValue } = useFormikContext<IAddIncomeForm>();

	const dispatch = useAppDispatch();

	useEffect(() => {
		let sum =
			Number(values?.SalaryAsPerSection17_1 || "0") +
			Number(values?.PerquisitesUnderSection17_2 || "0") +
			Number(values?.ProfitsInLieuOfSalaryUnderSection17_3 || "0") +
			Number(values?.IncomeUnderSection89ANotifiedCountry || "0") +
			Number(values?.IncomeUnderSection89AOtherThanNotifiedCountry || "0") +
			Number(values?.IncomeTaxableDuringThePreviousYear89A || "0");
		setFieldValue("GrossSalary", sum);
	}, [
		values.SalaryAsPerSection17_1,
		values.PerquisitesUnderSection17_2,
		values.ProfitsInLieuOfSalaryUnderSection17_3,
		values.IncomeUnderSection89ANotifiedCountry,
		values.IncomeUnderSection89AOtherThanNotifiedCountry,
		values.IncomeTaxableDuringThePreviousYear89A
	]);

	return (
		<>
			<div className="os-title-wrap">
				<h5 className="fw-bold">
					I. Gross Salary
					<span
						className="info-wrap"
						role="button"
						onClick={() => {
							dispatch(
								showHintModal({
									title: "Guide to fill Salary details",
									componentName: "GrossSalaryHint"
								})
							);
						}}
					>
						<FASvgIcon icon={faInfoCircle} />
					</span>
				</h5>
				<p className="leading-para mb-0">
					<PriceDisplay price={values?.GrossSalary || "0"} />
				</p>
			</div>
			<hr />
			<SalarySection17OneForm />
			<SalarySection17TwoForm />
			<SalarySection17ThreeForm />
			<SalarySection89ANotifiedForm />
			<SalarySection89ANonNotifiedForm />
			<SalarySection89APreYearForm />
		</>
	);
};

const ExemptAllowances = () => {
	const { values } = useFormikContext<IAddIncomeForm>();
	return (
		<>
			<div className="os-title-wrap">
				<h5 className="fw-bold">II. Allowances Exempt</h5>
				<p className="leading-para mb-0">
					<PriceDisplay price={values?.ExemptAllowancesUnderSection10 || "0"} />
				</p>
			</div>
			<hr />
			<SalaryExemptSection10Form />
		</>
	);
};

const Balance1Minus2 = () => {
	const { values, setFieldValue } = useFormikContext<IAddIncomeForm>();

	useEffect(() => {
		let sum =
			Number(values?.GrossSalary || "0") - Number(values?.ExemptAllowancesUnderSection10 || "0");
		// Number(values?.IncomeClaimedForReliefUnderSection89A || "0");
		setFieldValue("NetSalary", sum);
	}, [values.GrossSalary, values.ExemptAllowancesUnderSection10]);

	return (
		<>
			<div className="os-title-wrap">
				<h5 className="fw-bold">III. Net Salary (I - II)</h5>
				<p className="leading-para mb-0">
					<PriceDisplay price={Number(values?.NetSalary || "0")} />
				</p>
			</div>
			<hr />
		</>
	);
};

const DeductionsAndRelief = () => {
	const { values } = useFormikContext<IAddIncomeForm>();

	return (
		<>
			<div className="os-title-wrap">
				<h5 className="fw-bold">IV. Deductions</h5>
				<p className="leading-para mb-0">
					<PriceDisplay price={Number(values?.SalaryTotalDeductions || "0")} />
				</p>
			</div>
			<hr />
			<SalaryDeductionsAndReliefForm />
		</>
	);
};

const IncomeChargeableUnderHeadSalaries = () => {
	const { values, setFieldValue } = useFormikContext<IAddIncomeForm>();

	useEffect(() => {
		let sum =
			Number(values?.NetSalary || "0") -
			Number(values?.SalaryTotalDeductions || "0") -
			Number(values?.ReliefUnderSection89 || "0") -
			Number(values?.IncomeClaimedForReliefUnderSection89A || "0");
		setFieldValue("SalaryIncomeChargeable", sum);
	}, [
		values.NetSalary,
		values.SalaryTotalDeductions,
		values.ReliefUnderSection89,
		values.IncomeClaimedForReliefUnderSection89A
	]);

	return (
		<>
			<div className="os-title-wrap">
				<h5 className="fw-bold">VII. Income chargeable under head salaries (III-IV-V-VI)</h5>
				<p className="leading-para mb-0">
					<PriceDisplay
						price={
							Number(values?.SalaryIncomeChargeable || "0") > 0
								? Number(values?.SalaryIncomeChargeable || "0")
								: 0
						}
					/>
				</p>
			</div>
		</>
	);
};
